import axiosServices from '../utils/axios';
const backendUrl = import.meta.env.VITE_BACKEND_URL;
/** Same-origin in dev (Vite proxy + cookie). Staging/prod use API host + ``pt`` JWT on the stream URL. */
export const recordingApiBase = import.meta.env.DEV ? '' : backendUrl;

export const fetchInterviews = async (paginationModel) => {
  const response = await axiosServices.get(`${backendUrl}/qualitative-studies/`, {
    params: paginationModel,
  });
  return response.data;
};
export const fetchInterview = async (id) => {
  const response = await axiosServices.get(`${backendUrl}/qualitative-studies/${id}/`);
  return response.data;
};
export const updateInterviewFinalize = async ({ id, transformedData, processId }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${id}/?process_id=${processId}`,
    transformedData,
  );
  return response.data;
};
export const updateInterviewStatus = async ({ id, transformedData }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${id}/`,
    transformedData,
  );
  return response.data;
};
export const fetchInterviewDetails = async (id) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/${id}/finalizable_details/`,
  );
  return response.data;
};
export const fetchCodeBook = async (id) => {
  const response = await axiosServices.get(`${backendUrl}/qualitative-studies/${id}/codebook/`);
  return response.data;
};
export const fetchTranscripts = async (id) => {
  const response = await axiosServices.get(`${backendUrl}/qualitative-studies/${id}/transcripts/`);
  return response.data;
};
export const fetchTranscript = async (id) => {
  const response = await axiosServices.get(`${backendUrl}/qualitative-studies/${id}/transcripts/`);
  return response.data;
};
export const fetchAnalysis = async (id) => {
  const response = await axiosServices.get(`${backendUrl}/qualitative-studies/${id}/themes/`);
  return response.data;
};
export const createInterview = async (formData) => {
  // Validate input data
  if (!formData || typeof formData !== 'object') {
    throw new Error('Invalid form data provided');
  }

  if (!formData.title?.trim()) {
    throw new Error('Interview title is required');
  }

  const payload = {
    ...formData,
  };

  const response = await axiosServices.post(`${backendUrl}/qualitative-studies/`, payload);

  return response.data;
};
export const updateInterview = async ({ id, payload }) => {
  const response = await axiosServices.patch(`${backendUrl}/qualitative-studies/${id}/`, payload);
  return response.data;
};
export const stopCollectingResponsesInterview = async ({ interview_id, disable_reason }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/${interview_id}/disable-study/?disable_reason=${disable_reason}`,
  );
  return response.data;
};
export const deleteInterviews = async (id) => {
  const response = await axiosServices.delete(`${backendUrl}/qualitative-studies/${id}/`);
  return response.data;
};
export const duplicateInterview = async (input) => {
  const id = typeof input === 'object' ? input?.id : input;
  const title = typeof input === 'object' ? input?.title : null;
  const params = { study_id: id };

  if (title?.trim()) {
    params.title = title.trim();
  }

  const response = await axiosServices.post(`${backendUrl}/qualitative-studies/`, null, { params });
  return response.data;
};
export const createLinkBasedShareInterview = async ({ interviewId, returnUrl }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/link-based-share-request/${interviewId}/?return_url=${returnUrl}`,
  );
  return response.data;
};
export const revokeLinkBasedShareInterview = async ({ interviewId, token }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/link-based-share-request/${interviewId}/revoke/?token=${token}`,
  );
  return response.data;
};
export const analyzeInterview = async (
  studyId,
  analysis_lens_type,
  transcript_ids,
  analysis_level = '',
  analysis_item_id = null,
  workspaceId = null,
) => {
  // workspace_id required for atomic credit deduction (unless regeneration with analysis_item_id)
  const url = `${backendUrl}/qualitative-studies/${studyId}/analyses/lenses/run${workspaceId ? `?workspace_id=${workspaceId}` : ''}`;
  const payload = {
    analysis_lens_type,
    transcript_ids,
    analysis_level,
  };
  // Include analysis_item_id if provided (for regenerating themes/report)
  if (analysis_item_id) {
    payload.analysis_item_id = analysis_item_id;
  }
  const { data } = await axiosServices.post(url, payload);
  return data;
};

export const generateInterviewCodes = async (id) => {
  const response = await axiosServices.post(`${backendUrl}/qualitative-studies/${id}/codes/`);
  return response.data;
};
export const generateInterviewCodeBook = async (id) => {
  const response = await axiosServices.post(`${backendUrl}/qualitative-studies/${id}/codebook/`);
  return response.data;
};
export const generateInterviewReport = async (id) => {
  const response = await axiosServices.post(`${backendUrl}/qualitative-studies/${id}/report/`);
  return response.data;
};
export const getInterviewReport = async ({ interviewId, analysisItemId } = {}) => {
  // Pass analysis_item_id so the right run's report renders when a
  // study has multiple Grounded Emergence analyses. Server defaults
  // to "most recent in study" when omitted.
  const params = analysisItemId ? { analysis_item_id: analysisItemId } : undefined;
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/${interviewId}/report/`,
    { params },
  );
  return response.data;
};
export const generateInterviewThemes = async (id) => {
  const response = await axiosServices.post(`${backendUrl}/qualitative-studies/${id}/themes/`);
  return response.data;
};
export const generateInterviewId = async (formData) => {
  const response = await axiosServices.post(`${backendUrl}/qualitative-studies/`, formData);
  return response.data;
};
export const generateUploadUrl = async (formData) => {
  const response = await axiosServices.post(`${backendUrl}/upload/generate-upload-url`, formData);
  return response.data;
};
export const generateMultiUploadUrl = async (formData) => {
  const { study_id, ...rest } = formData;

  // If there's form data (from interview upload flow), use the base endpoint
  // Otherwise (from transcripts), use the endpoint with study_id
  const endpoint = rest.title
    ? `${backendUrl}/qualitative-studies/upload/generate-upload-url`
    : `${backendUrl}/qualitative-studies/upload/generate-upload-url?study_id=${study_id}`;

  const response = await axiosServices.post(endpoint, {
    ...rest,
  });
  return response.data;
};
export const commitBlocks = async (formData) => {
  const response = await axiosServices.post(`${backendUrl}/upload/commit-blocks`, formData);
  return response.data;
};
export const commitMultiBlocks = async (formData) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/upload/commit-blocks`,
    formData,
  );
  return response.data;
};
export const saveAndAnalyzeInterviewAudioVideo = async ({ id, processId, ...payload }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/save_and_transcript_generate/${id}/?process_id=${processId || ''}`,
    payload,
  );
  return response.data;
};
export const fetchTranscriptList = async ({
  studyId,
  filterList,
  pagination,
  statusFilter,
  typeFilter,
  sourceFilter,
}) => {
  const { limit = 15, offset = 0 } = pagination || {};
  const statusParam = statusFilter ? `&status_filter=${encodeURIComponent(statusFilter)}` : '';
  const typeParam = typeFilter ? `&type_filter=${encodeURIComponent(typeFilter)}` : '';
  const sourceParam = sourceFilter ? `&source_filter=${encodeURIComponent(sourceFilter)}` : '';
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/${studyId}/transcripts/?limit=${limit}&offset=${offset}&filter_list=${JSON.stringify(
      filterList,
    )}${statusParam}${typeParam}${sourceParam}`,
  );
  return response.data;
};
export const fetchTranscriptDetails = async ({ studyId, transcriptId }) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/${studyId}/transcripts/${transcriptId}/`,
  );
  return response.data;
};
export const fetchRecordingPlaybackUrl = async ({ studyId, transcriptId, source }) => {
  const response = await axiosServices.get(
    `${recordingApiBase}/qualitative-studies/${studyId}/transcripts/${transcriptId}/recording-access`,
    { params: { source } },
  );
  return response.data;
};
export const queueTranscriptWorkflow = async ({ transcriptId, payload }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/transcripts/${transcriptId}/workflows`,
    payload,
  );
  return response.data;
};
export const fetchTranscriptSummary = async ({ transcriptId, regenerate = false }) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/transcripts/${transcriptId}/summary`,
    regenerate ? { params: { regenerate: true } } : undefined,
  );
  return response.data;
};
export const fetchTranscriptMetaAnalysis = async ({ transcriptId }) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/transcripts/${transcriptId}/meta_analysis`,
  );
  return response.data;
};
export const fetchInterviewCodebook = async ({ studyId, analysisItemId } = {}) => {
  const params = analysisItemId ? { analysis_item_id: analysisItemId } : undefined;
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/${studyId}/codebook/`,
    { params },
  );
  return response.data;
};
export const fetchInterviewAnalysis = async ({ studyId, analysisItemId } = {}) => {
  const params = analysisItemId ? { analysis_item_id: analysisItemId } : undefined;
  const response = await axiosServices.get(`${backendUrl}/qualitative-studies/${studyId}/themes/`, {
    params,
  });
  return response.data;
};
export const fetchCodeQuotes = async ({ studyId }) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/${studyId}/codes_quotes/`,
  );
  return response.data;
};
export const deleteTranscript = async (id) => {
  const response = await axiosServices.delete(
    `${backendUrl}/qualitative-studies/transcript/${id}/`,
  );
  return response.data;
};
export const deleteCategory = async (payload) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${payload?.id}/categories/delete`,
    payload,
  );
  return response.data;
};
export const removeInterviewCode = async (payload) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${payload?.qualitative_item_id}/codes/remove`,
    payload,
  );
  return response.data;
};
export const removeCodeFromCategory = async (payload) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${payload?.study_id}/categories/codes/remove`,
    payload,
  );
  return response.data;
};
export const addCodeFromCategory = async (payload) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${payload?.study_id}/categories/codes/add`,
    payload,
  );
  return response.data;
};
export const deleteInterviewCode = async (payload) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${payload?.qualitative_item_id}/codes/delete`,
    payload,
  );
  return response.data;
};
export const addStudyTag = async ({ payload, studyId }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/studies/${studyId}/tags/`,
    payload,
  );
  return response.data;
};
export const addStudyTagOption = async ({ payload, studyId, tagId }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/studies/${studyId}/edit-tags-add-value/${tagId}/`,
    payload,
  );
  return response.data;
};
export const updateStudyTagOption = async ({ payload, studyId, tagId, valueId }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/studies/${studyId}/tag/${tagId}/?value_id=${valueId}`,
    payload,
  );
  return response.data;
};
export const assignUnassignTagInTranscript = async ({
  payload,
  studyId,
  transcriptId,
  speakerId,
}) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/studies/${studyId}/assign-unassign/transcript/${transcriptId}/tags/?assign=${payload.isAssignTag}${speakerId ? `&speaker_id=${speakerId}` : ''}`,
    payload,
  );
  return response.data;
};
export const assignTagInTranscript = async ({ payload, studyId, transcriptId, speakerId }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/studies/${studyId}/assign/transcript/${transcriptId}/tags/${speakerId ? `?speaker_id=${speakerId}` : ''}`,
    payload,
  );
  return response.data;
};
export const updateStudyTagAttribute = async ({ payload, studyId, tagId }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/studies/${studyId}/tag/${tagId}/`,
    payload,
  );
  return response.data;
};
export const addTag = async ({ payload, transcriptId }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/transcript/${transcriptId}/tags/`,
    payload,
  );
  return response.data;
};
export const fetchStudyTags = async ({ studyId, searchQuery }) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/studies/${studyId}/tags/fetch/?query=${searchQuery}`,
  );
  return response.data;
};
export const fetchAssignedTag = async (studyId, searchQuery) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/studies/${studyId}/assign_tags/?q=${searchQuery}`,
  );
  return response.data;
};
export const copyTagsFromStudy = async ({ targetStudyId, sourceStudyId }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/studies/${targetStudyId}/tags/copy-from/${sourceStudyId}`,
  );
  return response.data;
};
export const fetchLensSummaries = async ({ transcriptId }) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/transcripts/${transcriptId}/lens_summaries`,
  );
  return response.data;
};
export const deleteStudyTag = async ({ id, tagId, valueId }) => {
  const response = await axiosServices.delete(
    `${backendUrl}/qualitative-studies/studies/${id}/tag/${tagId}${valueId ? `/?value_id=${valueId}` : '/'}`,
  );

  return response.data;
};
export const updateTranscriptName = async ({ payload, transcriptId, id }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${id}/transcript/${transcriptId}/`,
    payload,
  );
  return response.data;
};
export const addSpeakerTag = async ({ payload, transcriptId, speakerId }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/transcript/${transcriptId}/tags/?speaker_id=${speakerId}`,
    payload,
  );
  return response.data;
};
export const udpateCodeDetails = async ({ payload, transcriptId }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${transcriptId}/codes/edit`,
    payload,
  );
  return response.data;
};
export const udpateCategoryDetails = async ({ payload }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${payload.study_id}/categories/update`,
    payload,
  );
  return response.data;
};
export const udpateThemeDetails = async ({ payload }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${payload.study_id}/themes/update`,
    payload,
  );
  return response.data;
};
export const updateSpeaker = async ({ payload, transcriptId }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${transcriptId}/change-speaker/`,
    payload,
  );
  return response.data;
};
export const updateSpeakerInfo = async ({ payload, transcriptId, speakerId }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${transcriptId}/speaker/${speakerId}/`,
    payload,
  );
  return response.data;
};
export const addNewParticipant = async ({ payload, transcriptId }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/${transcriptId}/create-participant/`,
    payload,
  );
  return response.data;
};
export const deleteSpeakerTag = async (payload) => {
  const response = await axiosServices.delete(
    `${backendUrl}/qualitative-studies/transcript/${payload?.transcriptId}/tag/${payload?.attribute}/?speaker_id=${payload.speakerId}`,
  );
  return response.data;
};
export const addNewCode = async ({ payload, transcriptId }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${transcriptId}/codes/create`,
    payload,
  );
  return response.data;
};
export const assignCode = async ({ payload, transcriptId }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${transcriptId}/codes/assign_codes`,
    payload,
  );
  return response.data;
};
export const generateWelcomeMessage = async (payload) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/predict_welcome_message`,
    payload,
  );
  return response.data;
};
export const editTranscriptText = async (payload) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/${payload?.qualitative_item_id}/update_text`,
    payload,
  );
  return response.data;
};
export const getTranscriptRecording = async (itemId) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/merge_recordings/${itemId}/`,
  );
  return response.data;
};
export const getChatHistory = async (studyId) => {
  const response = await axiosServices.get(`${backendUrl}/chats?study_id=${studyId}`);
  return response.data;
};
export const getChatHistoryById = async (chatId) => {
  const response = await axiosServices.get(`${backendUrl}/chat/${chatId}`);
  return response.data;
};
export const likeDislikeChat = async (payload) => {
  const response = await axiosServices.patch(
    `${backendUrl}/chat/${payload.chatId}/turn/${payload.turnId}/feedback`,
    payload.feedback,
  );
  return response.data;
};
export const fetchLatestChatId = async (studyId) => {
  try {
    const response = await axiosServices.get(`${backendUrl}/latest-chat-id?study_id=${studyId}`);
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      return null;
    }
    throw error;
  }
};
export const fetchCodeDetails = async ({ studyId, codeName }) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/analysis/${studyId}/code_info?code_name=${codeName}`,
  );
  return response.data;
};

export const markAsCompleted = async ({ studyId, transcriptId, isCompleted }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/change-completed-status`,
    {
      study_id: studyId,
      qualitative_item_id: transcriptId,
      is_completed: isCompleted,
    },
  );
  return response.data;
};

export const exportInterviewParticipantData = async (studyId) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/${studyId}/participant/export`,
  );
  return response.data;
};

export const searchTranscripts = async ({
  studyId,
  search_term,
  searchType,
  threshold,
  offset = 0,
  limit = 10,
}) => {
  const response = await axiosServices.post(`${backendUrl}/qualitative-studies/${studyId}/search`, {
    search_term,
    search_type: searchType,
    threshold,
    offset,
    limit,
  });
  return response.data;
};

export const predictResearchQuestions = async (payload) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/predict_research_questions`,
    payload,
  );
  return response.data;
};

export const predictInterviewGuidelines = async (payload) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/predict_interview_guidelines`,
    payload,
  );
  return response.data;
};

export const exportInterviewTranscripts = async (studyId, transcriptIds, exportFormat, scope) => {
  const body = {
    ...(transcriptIds && transcriptIds.length > 0 ? { transcript_ids: transcriptIds } : {}),
    ...(exportFormat ? { export_format: exportFormat } : {}),
    ...(scope ? { scope } : {}),
  };
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/${studyId}/transcripts/export`,
    body,
  );
  return response.data;
};

export const queueSemanticEmbedding = async ({ studyId, transcriptIds = [] }) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/studies/${studyId}/semantic-embeddings/queue`,
    {
      transcript_ids: transcriptIds,
    },
  );
  return response.data;
};

export const closeInterview = async (studyId) => {
  const response = await axiosServices.post(`${backendUrl}/qualitative-studies/${studyId}/close/`);
  return response.data;
};

// ---- Briefing (pre-interview intro screen) ----
// Backed by app/api/endpoints/qualitative_study/briefing.py in customer-api.
// The upload dance mirrors StudyStimulus's block-list pattern; see
// components/interview/forms/configuration/BriefingSection.jsx for the
// caller side.

export const generateBriefingUploadUrl = async (studyId, payload) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/${studyId}/briefing/upload-url`,
    payload,
  );
  return response.data;
};

export const commitBriefing = async (studyId, payload) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/${studyId}/briefing/commit`,
    payload,
  );
  return response.data;
};

export const patchBriefingEnabled = async (studyId, enabled) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${studyId}/briefing`,
    { enabled },
  );
  return response.data;
};

// Update the preamble text shown before the media on the participant
// briefing screen. Send an empty string to clear it.
export const patchBriefingPreamble = async (studyId, preambleText) => {
  const response = await axiosServices.patch(
    `${backendUrl}/qualitative-studies/${studyId}/briefing`,
    { preamble_text: preambleText },
  );
  return response.data;
};

export const deleteBriefing = async (studyId) => {
  const response = await axiosServices.delete(
    `${backendUrl}/qualitative-studies/${studyId}/briefing`,
  );
  return response.data;
};

// Used to poll briefing.status while a video is faststart-processing.
export const fetchBriefingStatus = async (studyId) => {
  const response = await axiosServices.get(`${backendUrl}/qualitative-studies/${studyId}/`);
  return response.data?.briefing ?? null;
};

export const generateStimulusUploadUrl = async (formData) => {
  const { study_id, ...rest } = formData;
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/stimulus/generate-upload-url?study_id=${study_id}`,
    rest,
  );
  return response.data;
};

export const commitStimulusBlocks = async (formData) => {
  const response = await axiosServices.post(
    `${backendUrl}/qualitative-studies/stimulus/commit-blocks`,
    formData,
  );
  return response.data;
};

export const deleteStimulusImage = async ({ studyId, stimulusId }) => {
  const response = await axiosServices.delete(
    `${backendUrl}/qualitative-studies/stimulus/delete-image?study_id=${studyId}&stimulus_id=${stimulusId}`,
  );
  return response.data;
};

export const getResumeLink = async ({ studyId, transcriptId, refresh = false }) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/${studyId}/transcripts/${transcriptId}/resume-link${refresh ? '?refresh=true' : ''}`,
  );
  return response.data;
};
